import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Avatar,
  Stack,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  AppRegistrationOutlined,
  AttachFile,
  CancelOutlined,
  ChatBubbleOutlineOutlined,
  ContentCopyOutlined,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  DocumentScannerOutlined,
  Download,
  DownloadOutlined,
  HistoryOutlined,
  InfoOutlined,
  KeyboardVoice,
  ModeOutlined,
  PictureAsPdfOutlined,
  Preview,
  RemoveShoppingCartOutlined,
  Send,
  SimCardDownloadOutlined,
  SummarizeOutlined,
  TrackChanges,
  ViewInArOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchIVRNotesById,
  fetchIVRDocuments,
  useIVRDocumentSlice,
  IVRNotes,
  uploadIVRNotes,
  resetIvrDocumentInfoList,
  resetIvrNotesList,
} from "../../../redux/slice/ivr/ivrDocumentSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import CustomItemList from "../../common/CustomItemList";
import CustomIconButton from "../../common/CustomIconButton";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import IVRNotePopup from "./IVRNotePopup";
import { useSelector } from "react-redux";
import { selectIsTokenAvailable, selectIsValueFoundInToken, setSelectedModule } from "../../../redux/slice/authSlice";
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, IVR_STATUS, ORDERTYPE, PATIENT_LIST_ACTIONS, PRODUCT_CATEGORY } from "../../../constants/applicationConstants";
import { RootState } from "../../../redux/store";
import { convertBase64, downloadBase64File, hideElement } from "../../../utils/Utility";
import CustomTextField from "../../common/CustomTextField";
import PDFViewer from "../../common/pdf/PDFViewer";
import { fetchOrderDocumentsByIds } from "../../../redux/slice/order/OrderSlice";
import IVRDocumentsUploadPopup from "./IVRDocumentsUploadPopup";
import { useNavigation } from "../../../hooks/useNavigation";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { getPatient } from "../../../redux/slice/patient/patientSlice";
import { rowSelectionStateInitializer } from "@mui/x-data-grid/internals";
import DocumentHistoryPopup from "../patients/DocumentHistoryPopup";
import SpeechToTextPopup from "../../common/SpeechToTextPopup";
import ActionMenu, { ActionItem } from "../../common/ActionMenu";
import OptionsPopup from "../../common/OptionsPopup";
import { cancelIVRById, deleteIVRById, fetchIVRById, useCreateIVRSlice } from "../../../redux/slice/ivr/createIVRSlice";
import { getProductConfigByProduct, ProductConfigRules } from "../../../service/ProductService";
import { PRODUCT_CONFIG_CODE, PRODUCT_CONFIG_TYPE, PRODUCT_CONFIG_VALUE } from "../../../constants/applicationConstants";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface FormatDocumentNameParams  {
  name: string;
  docId?: any;
};

const getConfigValue = (configRules: ProductConfigRules[], configType: string, configCode: string): string | null => {
  const rule = configRules.find(r => r.configType === configType && r.configCode === configCode);
  return rule?.value ?? null;
};

const IVRDetails = () => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { IVRNotesList, ivrDocumentInfoList } = useIVRDocumentSlice();
  const userSideMap: Record<string, boolean> = {};
  let lastAuditUserName: string | null = null;
  const {
    tokens: mTokens,
    isDark: isDarkModal,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  // Matches Details Documents/ACTION + PatientsList Export outlined chrome
  const outlineAccentBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.82rem",
    borderRadius: "10px",
    height: 36,
    px: 1.6,
    color: mTokens.accent,
    borderColor: mTokens.accent,
    backgroundColor: "transparent",
    whiteSpace: "nowrap" as const,
    letterSpacing: "0.04em",
    "&:hover": {
      backgroundColor: `${mTokens.accent}14`,
      borderColor: mTokens.accent,
      boxShadow: "none",
    },
    "& .MuiTypography-root": { color: `${mTokens.accent} !important` },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: mTokens.accent },
  };

  const { handleSubmit, setValue, getValues, control } = useForm<IVRNotes>();

  const [ivrId, setIvrId] = useState(0);
  const [ivrNumber, setIvrNumber] = useState("");
  const [ivrDocumentId, setIvrDocumentId] = useState<number>(0);
  const [patientName, setPatientName] = useState("");
  const [patientDOB, setPatientDOB] = useState("");
  const [lastUpdateTime, setLastUpdateTime] = useState("");
  const [showIVRNotePopup, setIVRNotePopup] = useState<{
    show: boolean;
    ivrId: number;
  }>({ show: false, ivrId: 0 });
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const [documentData, setDocumentData] = useState<any[]>([]);
    const [ivrStatus, setIvrStatus] = useState("");

  const canNoteIVR = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.NOTE)(state)
  );
  const canViewHistory = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPatientActions", PATIENT_LIST_ACTIONS.HISTORY)(state));

  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    noteName: string;
    fileName: string;
  }>({ isShow: false, noteName: "", fileName: "" });

  const [pdfContent, setPDFContent] = useState<string>("");
  const [isIVRDocumentPopupOpen, setIVRDocumentPopupOpen] = useState(false);
  const [ivrInfo, setIvrInfo] = useState<any>();
  const [secondaryInsuranceAvailable, setSecondaryInsuranceAvailable] = useState(false);
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [documentTypes, setDocumentTypes] = useState<CodeTable[]>([]);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [selectedHistoryData, setSelectedHistoryData] = useState<any[]>([]);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{message:string, show: boolean, row: any}>({message: "", show: false, row: ""});
  const [cancelConfirm, setCancelConfirm] = useState<{message:string, show: boolean, row: any}>({message: "", show: false, row: ""});
  const { IVRObject} = useCreateIVRSlice();

  const canCreateDirectOrder = useSelector((state: RootState) => selectIsTokenAvailable("canCreateDirectOrder")(state));
  const canEditIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.EDIT)(state));
  const canRespondIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.RESPOND)(state));
  const canViewIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.VIEW)(state));
  const canCopyIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.COPY)(state));
  const canCancelIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.CANCEL)(state));
  const canDeleteIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.DELETE)(state));
  const canAuditIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.IVRAUDIT)(state));

  useEffect(() => {
    const ivrId = location?.state?.ivrInfo?.ivrId;
    if (ivrId) {
      dispatch(fetchIVRById({ivrId: ivrId, isSaveAuditLog: true}))
    }
    setIvrDocumentId(location?.state?.ivrInfo?.ivrDocumentId);
    setIvrInfo(location?.state?.ivrInfo);
    setIvrId(ivrId);
    setValue("xrefId", ivrId.toString());
    setIvrNumber(location?.state?.ivrInfo?.ivrNumber);
    setPatientName(location?.state?.ivrInfo?.patient);
    setPatientDOB(location?.state?.ivrInfo?.patientDOB);
    setLastUpdateTime(location?.state?.ivrInfo?.dateCreated);
    setIvrStatus(location?.state?.ivrInfo?.status);
    dispatch(fetchIVRNotesById(ivrId));
    dispatch(
      fetchIVRDocuments({
        ivrId: location?.state?.ivrInfo?.ivrId,
        patientId: location?.state?.ivrInfo?.patientId,
      })
    );
    fetchPatientDocuments(location?.state?.ivrInfo?.patientId);
  }, [location, dispatch]);

    //Unmount
    useEffect(() => () => {
      dispatch(resetIvrDocumentInfoList());
      dispatch(resetIvrNotesList());
    }, []);

    const fetchPatientDocuments = async (patientId:string) => {
      try {
        const response = await dispatch(getPatient({ patientId: patientId.toString() })).unwrap();
    
        const documents = response?.data?.patientInfo
          ?.filter(
            (info: { patientinfoData: { documentIds?: string[]; ivrDocumentListDTOs?: { documentName: string; auditTimestamp: string }[] } }) =>
              Array.isArray(info.patientinfoData?.documentIds) &&
              Array.isArray(info.patientinfoData?.ivrDocumentListDTOs) &&
              info.patientinfoData.documentIds.length > 0 &&
              info.patientinfoData.ivrDocumentListDTOs.length > 0
          )
          ?.flatMap((info:{ infoType: string; patientinfoData: { documentIds: any[]; ivrDocumentListDTOs?: any[] } }) =>
            info.patientinfoData.documentIds.map((docId:string, index:number) => {
              const documentEntry = info.patientinfoData.ivrDocumentListDTOs?.[index];
              if (!documentEntry) {
                return null; // Skip if no matching ivrDocumentListDTOs entry
              }
    
              return {
                infoType: info.infoType,
                documentId: docId,
                documentName: documentEntry.documentName,
                auditTimestamp: documentEntry.auditTimestamp,
                isDeleted: documentEntry.deletedDate ? true : false,
                isActive: documentEntry.isActive ?? false,
              };
            }).filter(Boolean) // Remove null entries
          );
    
          const docMap = new Map();

          for (const doc of documents) {
            const existing = docMap.get(doc.documentId);
            if (!existing || (!existing.isActive && doc.isActive)) {
              docMap.set(doc.documentId, doc);
            }
          }
      
          const finalDocuments = Array.from(docMap.values()).sort((a, b) => {
            return (b.isActive === true ? 1 : 0) - (a.isActive === true ? 1 : 0);
          });
      
          setDocumentData(finalDocuments);

        const hasSecondaryInsurance = response?.data?.patientInfo
        .find((info : {infoType: string}) => info.infoType === "SECONDARY_INSURANCE");
        if(hasSecondaryInsurance){
          setSecondaryInsuranceAvailable(true);
        } else {
          setSecondaryInsuranceAvailable(false);
        }
    
      } catch (error) {
        console.error("Error fetching patient documents:", error);
      }
    };

  const handleActionDownload = async (row: any, type: string) => {
    const documentId = type === "note" ? row.attachementId : row.documentId;
    if (documentId) {
      const actionResult = await dispatch(fetchDocumentById(documentId.toString()));
      const documentData = actionResult.payload;
      if (documentData?.content) {
        downloadBase64File(documentData.content, documentData.documentName, documentData.fileType);
      } else {
        dispatch(setSingleAlertObj({ message: "Document not found!", alertType: "info" }));
      }
    }
  };

  const handleActionPreview = async (row: any, type: string) => {
    const documentId = type === "note" ? row.attachementId : row.documentId;
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds(documentId)
      );
      const documentData = actionResult.payload;
      if (documentData && documentData.content) {
        // downloadBase64File(
        //   documentData.content,
        //   documentData.documentName,
        //   documentData.fileType
        // );

        setPDFContent(documentData.content);
        const filteredDocumentType = documentTypes.find((type) => type.keyName === row.documentType)?.displayValue || row.documentType;
        setShowPDF({
          isShow: true,
          noteName: patientName + " - " + (row.auditUserName||filteredDocumentType), // CS-1240
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };

  const handleCreateOrder = async () => {
    if (IVRObject) {
      dispatch(setSelectedModule("Order"));

      let productConfigRules: ProductConfigRules[] = [];
      try {
        const response = await getProductConfigByProduct(IVRObject.productId);
        productConfigRules = response?.data?.data ?? [];
      } catch (error) {
        console.error("Failed to fetch product config rules:", error);
      }

      const orderWorkflowType = getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_WORKFLOW, PRODUCT_CONFIG_CODE.TYPE);
      const isAncillaryWorkflow = orderWorkflowType === PRODUCT_CONFIG_VALUE.COLLAGEN;
      const isAncillaryByCategory = IVRObject.productCategory === PRODUCT_CATEGORY.CONSERVATIVE;
      const isAncillary = orderWorkflowType ? isAncillaryWorkflow : isAncillaryByCategory;

      if (isAncillary) {
        const response = await dispatch(getPatient({ patientId: IVRObject?.patient.patientId.toString() }));
        navigate("/ancillaryorder", { state: { ivrId: IVRObject?.ivrId, practiceId: IVRObject?.practiceId, practiceName: IVRObject?.ivrInfo.providerInfo.practiceName, patientId: IVRObject?.patient.patientId, providerId: IVRObject?.providerId, providerName: IVRObject?.ivrInfo.providerInfo.provider.providerName, orderType: ORDERTYPE.DIRECT_ORDER, patientData: response.payload.data } });
      } else {
        const orderDetails = {
          dateOfService: IVRObject.dateOfService,
          ivrId: IVRObject.ivrId,
          ivrNumber: IVRObject.ivrNumber,
          locationId: IVRObject.locationId,
          patientCode: IVRObject.patient.patientCode,
          patientDOB: IVRObject.patient.birthDate,
          patientFirstName: IVRObject.patient.firstName,
          patientId: IVRObject.patient.patientId,
          patientLastName: IVRObject.patient.lastName,
          patientMiddleName: IVRObject.patient.middleName,
          practiceId: IVRObject.practiceId,
          practiceName: IVRObject.ivrInfo?.providerInfo?.practiceName || "",
          productCategory: IVRObject.productCategory,
          productId: IVRObject.productId,
          providerId: IVRObject.providerId,
          providerName: IVRObject.ivrInfo?.providerInfo?.provider?.providerName || "",
          verificationStatus: IVRObject.verificationStatus,
        };
        navigate("/order", { state: { fromUI: "IVRList", orderType: ORDERTYPE.DIRECT_ORDER, orderDetails: orderDetails } });
      }
    }
  }

  const confirmCancelIVR = async () => {
    const response = await dispatch(cancelIVRById(cancelConfirm.row));
    if (response.payload.message === "SUCCESS") {
      const alertObj: SingleAlertInfo = {
        message: `IVR  ${IVRObject?.ivrNumber} has been cancelled successfully.`,
        alertType: "success",
      };
      dispatch(fetchIVRById({ivrId:location?.state?.ivrInfo?.ivrId, isSaveAuditLog: false}))
    }
    setCancelConfirm({ message: "", show: false, row: "" });
  }


  const confirmDeleteIVR = async () => {
    const response = await dispatch(deleteIVRById(deleteConfirm.row));
    if (response.payload.message === "SUCCESS") {
      const alertObj: SingleAlertInfo = {
        message: `IVR ${IVRObject?.ivrNumber} has been deleted successfully.`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      navigate("/ivr_list", { state: { withSearchHistory: true } });
    }
    setDeleteConfirm({ message: "", show: false, row: "" });
  }

  const cancelDeleteIVR = () => {
    setDeleteConfirm({ message: "", show: false, row: "" });
  }

  const cancelCancelIVR = () => {
    setCancelConfirm({ message: "", show: false, row: "" });
  }

  const handleClickIVRAudit = () => {
    if (IVRObject && IVRObject?.ivrNumber) {
      const IVRAuditData = {
        entityName: "IVR",
        entityNumber: IVRObject?.ivrNumber
      }

      const url = `/auditlog?entityName=${IVRAuditData.entityName}&entityNumber=${IVRAuditData.entityNumber}`;
      window.open(url, '_blank');
      // navigate("/auditlog", '_blank',{ state: { entityName: IVRAuditData.entityName, entityNumber: IVRAuditData.entityNumber } });
    }
  }

  const actions: ActionItem[] = [
    {
      label: "Create Order",
      icon: <AppRegistrationOutlined fontSize="small" />,
      color: "success",
      onClick: () => handleCreateOrder(),
      isHidden: !canCreateDirectOrder || IVRObject?.verificationStatus !== IVR_STATUS.VERIFIED
    },
    {
      label: "Edit",
      icon: <ModeOutlined fontSize="small" />,
      color: "info",
      onClick: () => navigate("/ivr", {
        state: {
          ivrId: IVRObject?.ivrId, practiceId: IVRObject?.practiceId, ivrStatus: IVRObject?.verificationStatus, ivrNumberLink: IVRObject?.ivrNumber,
          patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: IVRObject?.patient.patientId }
        }
      }),
      isHidden: !canEditIVR || ![IVR_STATUS.DRAFTED].includes(IVRObject?.verificationStatus ?? "")
    },
    {
      label: "Respond",
      icon: <SummarizeOutlined fontSize="small" />,
      color: "warning",
      onClick: () => {
        const orderDetails = {
          ivrId: IVRObject?.ivrId,
          ivrNumber: IVRObject?.ivrNumber,
          patient: `${IVRObject?.patient.firstName} ${IVRObject?.patient.middleName ? IVRObject.patient.middleName + ' ' : ''}${IVRObject?.patient.lastName}`,
          patientDOB: IVRObject?.patient.birthDate,
        };
        navigate("/benefit_summary", { state: { orderDetails } });
      },
      isHidden: !canRespondIVR || (IVRObject?.verificationStatus !== IVR_STATUS.SUBMITTED && IVRObject?.verificationStatus !== IVR_STATUS.NEEDS_MORE_INFO && IVRObject?.verificationStatus !== IVR_STATUS.PRE_AUTH)
    },
    {
      label: "View",
      icon: <ViewInArOutlined fontSize="small" />,
      color: "info",
      onClick: () => navigate("/ivr", {
        state: {
          ivrId: IVRObject?.ivrId, viewOnly: true, practiceId: IVRObject?.practiceId,
          patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: IVRObject?.patient.patientId }
        }
      }),
      isHidden: !canViewIVR || ![IVR_STATUS.SUBMITTED, IVR_STATUS.DENIED, IVR_STATUS.NEEDS_MORE_INFO, IVR_STATUS.PRE_AUTH, IVR_STATUS.VERIFIED, IVR_STATUS.PRE_AUTH].includes(IVRObject?.verificationStatus ?? "")
    },
    {
      label: "Copy IVR",
      icon: <ContentCopyOutlined fontSize="small" />,
      color: "info",
      onClick: () => navigate("/ivr", {
        state: {
          ivrId: IVRObject?.ivrId, practiceId: IVRObject?.practiceId, duplicateIVR: true,
          patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: IVRObject?.patient.patientId }
        }
      }),
      isHidden: !canCopyIVR || [IVR_STATUS.DRAFTED].includes(IVRObject?.verificationStatus ?? "")
    },
    {
      label: "Cancel",
      icon: <RemoveShoppingCartOutlined fontSize="small" />,
      color: "error",
      onClick: () => { setCancelConfirm({ message: "Are you sure you want to cancel this IVR?", show: true, row: IVRObject?.ivrId }); },
      isHidden: !canCancelIVR || [IVR_STATUS.DRAFTED, IVR_STATUS.CANCELED].includes(IVRObject?.verificationStatus ?? "")
    },
    {
      label: "Delete",
      icon: <DeleteOutlineOutlined fontSize="small" />,
      color: "error",
      onClick: () => { setDeleteConfirm({ message: "Are you sure you want to delete this IVR?", show: true, row: IVRObject?.ivrId }); },
      isHidden: !canDeleteIVR || ivrStatus !== IVR_STATUS.DRAFTED
    },
    {
      label: "IVR Audit",
      icon: <TrackChanges fontSize="small" />,
      color: "warning",
      onClick: () => handleClickIVRAudit(),
      isHidden: !canAuditIVR
    }
  ];

  const hasVisibleActions = useMemo(() => {
    return actions.some(action => !action.isHidden);
  }, [actions]);

  const isFileAttached = (row: any) => (row.attachementId ? "" : "none");

  const documentColumns = [
    { id: "documentType", label: "Doc Type" },
    { id: "documentName", label: "Doc Name" },
    { id: "auditTimestamp", label: "Time" },
    {
      id: "action",
      label: "Download",
      align: "center" as "center" | "left" | "right",
    },
  ];

    const documentRows = (ivrStatus === "DRAFTED" ? documentData : ivrDocumentInfoList)?.map((row: any) => ({
      // const documentRows = (documentData)?.map((row: any) => ({
      ...row,
      action: (
        <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleActionDownload(row, "document")}
          tooltipMessage="Download"
          tooltipPlacement="bottom"
        >
          <Download />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleActionPreview(row, "document")}
          tooltipMessage="Preview"
          tooltipPlacement="bottom"
        >
          <Preview />
        </CustomIconButton>
        </>
      ),
    }));

    const handleHistoryPopup = () => {
      setShowHistoryPopup((prev) => !prev);
    };

    const getHistoryForType = (documentInfoType: string) => {
      if (!documentInfoType) return [];
  
      const filteredDocs = documentData.filter(
        doc => doc.infoType === documentInfoType && !(doc.isActive && !doc.isDeleted)
      );
  
      return filteredDocs.map(doc => ({
        name: doc.documentName,
        type: doc.infoType,
        docId: doc.documentId,
        date: new Date(doc.auditTimestamp).toLocaleString(),
        status: doc.isDeleted
          ? "Deleted"
          : doc.isActive
            ? "Active"
            : "Inactive",
      }));
    };
  
    const handleActionHistory = (documentInfoType: string) => {
      const history = getHistoryForType(documentInfoType);
      if (history.length > 0) {
        setSelectedHistoryData(history);
        handleHistoryPopup();
      }
    };

    const groupedDocuments = documentRows?.reduce((groups: any, doc: any) => {
      const groupKey = doc.infoType ? doc.infoType : doc.documentType;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(doc);
      return groups;
    }, {});

  const downloadAll = async () => {
    const dataSource = ivrStatus === "DRAFTED" ? documentData : ivrDocumentInfoList;
    if (!dataSource) return;
  
    const zip = new JSZip();
    const documentsFolder = zip.folder("documents");
    if (!documentsFolder) {
      console.error("Failed to create documents folder in the zip archive.");
      return;
    }
    for (const row of dataSource) {
      if (row.documentId) {
        await downloadAndAddToZip(row.documentId.toString(), documentsFolder);
      }
    }
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "all_documents.zip");
    });
  };

  const downloadAndAddToZip = async (documentId: string, zipFolder: JSZip) => {
    const actionResult = await dispatch(
      fetchDocumentById(documentId.toString())
    );
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      const byteCharacters = atob(documentData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      zipFolder.file(documentData.documentName, byteArray, { binary: true });
    } else {
      console.log(`Document with ID ${documentId} not found or is empty.`);
    }
  };

  useEffect(() => {
    dispatch(setSelectedModule("IVR"));
    resetData();
  }, []);

  const resetData = () => {
    setValue("attachment", "");
    setValue("xrefTable", "IVR");
    setValue("noteType", "NOTE");
    setValue("content", "");
    handleRemoveFile();
  };

  const onSubmit = async (data: IVRNotes) => {
    setValue("xrefId", ivrId.toString());
    try {
      const response = await dispatch(uploadIVRNotes(data));
      if (
        response.payload &&
        response.payload.message === "Note is added successfully"
      ) {
        let alertMessage = "Note saved Successfully!";
        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        dispatch(fetchIVRNotesById(ivrId));
        resetData();
      }
    } catch (error) {
      console.log("Error saving note:", error);
    }
  };

  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  // const handleNotDocumentFileChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   try {
  //     const files = event.target.files;
  //     if (files && files.length > 0) {
  //       const file = files[0];
  //       const base64File = await convertBase64(file);

  //       setValue("attachment", removeDataUriPrefix(String(base64File)));
  //       setValue("documentName", file.name);
  //       setValue("fileType", file.type);
  //       setValue("noteType", "NOTE");
  //       setValue("xrefTable", "IVR");
  //       setSelectedFileName(file.name);
  //     } else {
  //       console.error("No files selected or invalid input.");
  //     }
  //   } catch (error) {
  //     console.error("Error processing the file:", error);
  //   }
  // };

  const handleNotDocumentFileChange = async (file: any) => {
    try {
      setValue("attachment", file.content);
      setValue("documentName", file.documentName);
      setValue("fileType", file.fileType);
      setValue("noteType", "NOTE");
      setValue("xrefTable", "IVR");
      setSelectedFileName(file.documentName);
      
      setIVRDocumentPopupOpen(false);
    } catch (error) {
      console.error("Error processing the file:", error);
      dispatch(
        setSingleAlertObj({
          message: "Failed to process the selected file.",
          alertType: "error",
        })
      );
    }
  };

  // CS-991 Move PDF from action to Details Pages
  const handlePreviewAction = async (row: any) => {
    const actionResult = await dispatch(fetchDocumentById(row.ivrDocumentId));
    const documentData = actionResult.payload;
    
    if (documentData && documentData.content) {
      setPDFContent(documentData.content);
      setShowPDF({ isShow: true, noteName: row.product + ` - IVR`, fileName: row.ivrNumber });
    };
  }
  const handleRemoveFile = () => {
    setSelectedFileName(null);
    setValue("attachment", "");
  };

  // const documentTypeOptions = useMemo(() => {
  //   return documentTypes.map(item => ({
  //     label: item.displayValue,
  //     value: item.keyName
  //   }));
  // }, [documentTypes]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentTypes = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "documentType"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setDocumentTypes(sortedDocumentTypes);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const formatDocumentName = (data: FormatDocumentNameParams) => {
    const maxLength = 20;
    if (!data.name) return `Document_${data.docId}`;
    
    const lastDotIndex = data.name.lastIndexOf('.');
    
    if (lastDotIndex > 0) {
      const baseName = data.name.substring(0, lastDotIndex);
      const extension = data.name.substring(lastDotIndex);
      
      if (baseName.length > (maxLength)) {
        return `${baseName.substring(0, maxLength)}...${extension}`;
      }
      return data.name;
    } 
    else if (data.name.length > (maxLength)) {
      return `${data.name.substring(0, maxLength)}...`;
    }
    return data.name;
  };

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
      {showIVRNotePopup?.show && (
        <IVRNotePopup
          open={showIVRNotePopup.show}
          ivrId={showIVRNotePopup.ivrId}
          onClose={() => setIVRNotePopup({ show: false, ivrId: 0 })}
        />
      )}
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, noteName: "", fileName: "" })
          }
          title={showPDF.noteName}
          fileName={showPDF.fileName}
          base64String={pdfContent || ""}
        />
      )}

      {isIVRDocumentPopupOpen && (
        <IVRDocumentsUploadPopup
          open={isIVRDocumentPopupOpen}
          onClose={() => {setIVRDocumentPopupOpen(false)}}
          onDocumentSelected={handleNotDocumentFileChange}
          ivrId={ivrId}
          patientId={location?.state?.ivrInfo?.patientId}
          onUploadSuccess={() => fetchPatientDocuments(location?.state?.ivrInfo?.patientId)}
          hasSecondaryInsurance = {secondaryInsuranceAvailable}
        />
      )}

      {isVoicePopupOpen && (
        <SpeechToTextPopup
          open={isVoicePopupOpen}
          onClose={() => {setIsVoicePopupOpen(false)}}
          onSubmitVoice={(data: any)=>setValue("content",data)}
        />
      )}

      <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <PageBackButton tooltip={null} onClick={() => navigate(-1, { state: { withSearchHistory: true } })} />
          <Box data-testid="header">
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
              IVR / IVR Details
            </Typography>
            <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
              IVR Details
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            removeUpperCase
            onClick={() => ivrInfo && handlePreviewAction(ivrInfo)}
            style={{ display: hideElement(ivrDocumentId === 0) ? "none" : "" }}
            sx={outlineAccentBtnSx}
            startIcon={<PictureAsPdfOutlined fontSize="small" />}
          >
            IVR PDF
          </Button>
          <ActionMenu
            label="Action"
            actions={actions}
            disabled={!hasVisibleActions}
            removeUpperCase
            sx={outlineAccentBtnSx}
          />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ ...cardSx, order: { xs: 2, md: 1 } }}>
          <SectionHeader
            icon={<DescriptionOutlined fontSize="small" />}
            title="Patient Documents"
            action={
              documentRows && documentRows.length > 0 ? (
                <Button
                  startIcon={<DownloadOutlined />}
                  variant="outlined"
                  removeUpperCase
                  onClick={downloadAll}
                  sx={outlineAccentBtnSx}
                >
                  Download All
                </Button>
              ) : undefined
            }
          />
          <Box sx={{ px: 2.8, py: 1.8 }}>
              {documentRows && documentRows.length > 0 ? (
                Object.keys(groupedDocuments).map((docType, index) => {
                  const group = groupedDocuments[docType];
                  const filteredDocumentType =
                    documentTypes.find((type) => type.keyName === docType)?.displayValue || docType;

                  return (
                    <Box key={index}>
                      {group.map((row: any, idx: any) => {
                        const fadedStyle = row.isActive === false ? mTokens.textSecondary : mTokens.textPrimary;
                        if (ivrStatus==="DRAFTED" && !row.isActive) return null;
                        return (
                          <Grid
                            key={idx}
                            container
                            spacing={2}
                            alignItems="center"
                            sx={{ paddingY: 1.2, overflowX: "auto" }}
                          >
                            <Grid item md={2.5} xs={12}>
                              {idx === 0 && (
                                <>
                                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>Doc Type</Typography>
                                  <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600 }}>
                                    {filteredDocumentType}
                                    {" "}
                                    {getHistoryForType(row.infoType).length > 0 && (
                                      <CustomIconButton
                                        color="warning"
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleActionHistory(row.infoType || row.documentType)}
                                        tooltipMessage="History"
                                        tooltipPlacement="bottom"
                                        style={{ display: `${hideElement(!canViewHistory || row.documentType === "COMPLETED_IVR")}` }}
                                      >
                                        <HistoryOutlined />
                                      </CustomIconButton>
                                    )}
                                  </Typography>
                                </>
                              )}
                            </Grid>
                            <Grid item md={3.5} xs={12}>
                              {idx === 0 && (
                                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>Doc Name</Typography>
                              )}
                              <Tooltip title={row.documentName} placement="top" arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.88rem",
                                    color: fadedStyle,
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    maxWidth: "100%",
                                    cursor: "default",
                                  }}
                                >
                                  {row.documentName}
                                </Typography>
                              </Tooltip>
                              {/*  */}
                            </Grid>
                            <Grid item md={3} xs={9}>
                              {idx === 0 && (
                                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>Time</Typography>
                              )}
                              <Typography 
                                sx={{
                                  fontSize: "0.85rem",
                                  color: fadedStyle,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.auditTimestamp}
                              </Typography>
                            </Grid>
                            <Grid item md={3} xs={12}>
                              <Box
                                sx={{
                                  display: { xs: "flex", sm: "block" },
                                  flexDirection: { xs: "column", sm: "row" },
                                  alignItems: { xs: "flex-start", sm: "center" },
                                  width: "100%",
                                }}
                              >
                                {idx === 0 && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em",
                                      display: { xs: "block", sm: "block" },
                                      mb: { xs: 0.8, sm: 0.3 },
                                    }}
                                  >
                                    Action
                                  </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 1,
                                    mt: { xs: 0.5, sm: 0 },
                                  }}
                                >
                                  {row.action}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })}
                      {index < Object.keys(groupedDocuments).length - 1 && (
                        <Box
                          sx={{
                            borderBottom: "1px dashed",
                            borderColor: mTokens.border,
                            my: 1,
                          }}
                        />
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  textAlign="center"
                  minHeight="150px"
                  >
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: mTokens.textSecondary }}>No documents available.</Typography>
                </Box>
              )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 1, md: 2 } }}>
          <Box sx={cardSx}>
            <SectionHeader icon={<InfoOutlined fontSize="small" />} title="Info" />
            <Box sx={{ px: 2.8, py: 1.5 }}>
              <InfoRow label="IVR ID" value={ivrNumber} />
              <InfoRow label="Patient" value={patientName} />
              <InfoRow label="DOB" value={patientDOB} />
              <InfoRow label="Last Update Date" value={lastUpdateTime} />
            </Box>
          </Box>

          <Box sx={cardSx}>
            <SectionHeader icon={<ChatBubbleOutlineOutlined fontSize="small" />} title="Notes" />
            <Box
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
                {IVRNotesList?.map((note: any, index: number) => {
                  if (!(note.auditUserName in userSideMap)) {
                    userSideMap[note.auditUserName] =
                      lastAuditUserName === null ||
                      !userSideMap[lastAuditUserName];
                  }
                  const isLeft = userSideMap[note.auditUserName];
                  lastAuditUserName = note.auditUserName;

                  return (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent={isLeft ? "flex-start" : "flex-end"}
                      p={2}
                    >
                      {isLeft && (
                        <Avatar
                          sx={{
                            marginRight: 1,
                            fontSize: 20,
                            fontWeight: "bold",
                            backgroundColor: mTokens.accent,
                          }}
                        >
                          {note.auditUserName.charAt(0)}
                        </Avatar>
                      )}
                      <Box
                        sx={{
                          padding: 1.4,
                          backgroundColor: isDarkModal ? "#1c252e" : mTokens.headerFooterBg,
                          minWidth: "30%",
                          maxWidth: "100%",
                          borderRadius: isLeft
                            ? "1px 10px 10px 10px"
                            : "10px 1px 10px 10px",
                          border: `1px solid ${mTokens.border}`,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: mTokens.textPrimary }}>
                          {note.auditUserName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, mt: 1 }}>
                          {note.content}
                        </Typography>

                        {note.attachementId && (
                          <Stack
                            alignItems="center"
                            direction="row"
                            gap={1}
                            onClick={() => handleActionPreview(note, "note")}
                            mt={1}
                            sx={{ cursor: "pointer", color: mTokens.accent }}
                          >
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                              {note.documentName}
                            </Typography>
                             <Tooltip title="View File" placement="top" arrow>
                               <Box display="flex">
                                 <DocumentScannerOutlined sx={{ fontSize: 22 }} />
                               </Box>
                             </Tooltip>
                          </Stack>
                        )}
                        <Box display="flex" justifyContent="flex-end">
                          <Typography sx={{ fontSize: "0.7rem", color: mTokens.textSecondary, mt: 0.5 }}>
                            {note.auditTimestamp}
                          </Typography>
                        </Box>
                      </Box>

                      {!isLeft && (
                        <Avatar
                          sx={{
                            marginLeft: 1,
                            fontSize: 20,
                            fontWeight: "bold",
                          }}
                        >
                          {note.auditUserName.charAt(0)}
                        </Avatar>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Divider sx={{ borderColor: mTokens.border }} />
              <Box
                sx={{
                  p: 2,
                  backgroundColor: mTokens.headerFooterBg,
                }}
              >
                <Box style={{ display: `${hideElement(!canNoteIVR)}` }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1,
                      py: 0.5,
                      minHeight: 44,
                      borderRadius: "12px",
                      border: `1px solid ${mTokens.border}`,
                      backgroundColor: mTokens.bg,
                    }}
                  >
                    <Tooltip title="Speech to text" placement="top" arrow>
                      <IconButton
                        color="default"
                        component="label"
                        sx={{
                          width: 36,
                          height: 36,
                          p: 0,
                          borderRadius: "10px",
                          bgcolor: mTokens.accent,
                          transition: "background-color 0.4s ease",
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: "#0d9488",
                          },
                        }}
                        onClick={() => setIsVoicePopupOpen(true)}
                      >
                        <KeyboardVoice sx={{ color: "#ffffff", fontSize: "1.15rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Attach File" placement="top" arrow>
                      <IconButton
                        color="default"
                        component="label"
                        sx={{
                          width: 36,
                          height: 36,
                          p: 0,
                          borderRadius: "10px",
                          border: `1px solid ${mTokens.border}`,
                          backgroundColor: mTokens.headerFooterBg,
                          color: mTokens.textSecondary,
                          flexShrink: 0,
                          "&:hover": {
                            borderColor: mTokens.accent,
                            color: mTokens.accent,
                            backgroundColor: `${mTokens.accent}0d`,
                          },
                        }}
                        onClick={() => setIVRDocumentPopupOpen(true)}
                      >
                        <AttachFile sx={{ fontSize: "1.15rem" }} />
                        {/* <input
                        type="file"
                        hidden
                        onChange={handleNotDocumentFileChange}
                      /> */}
                      </IconButton>
                    </Tooltip>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        "& .MuiFormControl-root": { m: 0, width: "100%" },
                        "& .MuiInputBase-root.MuiInputBase-multiline": {
                          padding: "0 !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          alignItems: "center",
                          minHeight: "36px !important",
                          padding: "0 !important",
                          backgroundColor: "transparent !important",
                          boxShadow: "none !important",
                          borderRadius: "8px !important",
                        },
                        "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
                          border: "none !important",
                        },
                        "& .MuiInputLabel-root, & label": { display: "none" },
                        "& .MuiOutlinedInput-root textarea, & .MuiInputBase-inputMultiline": {
                          boxSizing: "border-box !important",
                          minHeight: "36px !important",
                          height: "auto !important",
                          maxHeight: "96px !important",
                          padding: "8px 10px !important",
                          lineHeight: "20px !important",
                          fontSize: "0.88rem",
                          color: mTokens.textPrimary,
                          resize: "none",
                          overflowY: "auto",
                        },
                      }}
                    >
                      <CustomTextField
                        controllerName="content"
                        control={control}
                        label=""
                        placeholder="Write a note..."
                        multiline
                        minRows={1}
                        maxRows={4}
                        size="small"
                        rules={{ required: "Notes are required." }}
                        inputProps={{ "aria-label": "Notes" }}
                      />
                    </Box>
                    <IconButton
                      sx={{
                        width: 36,
                        height: 36,
                        p: 0,
                        borderRadius: "10px",
                        backgroundColor: mTokens.accent,
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "#0d9488",
                        },
                      }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      <Send sx={{ color: "#ffffff", fontSize: "1.15rem" }} />
                    </IconButton>
                  </Box>
                  {selectedFileName && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        mt: 1.2,
                        px: 1.2,
                        py: 0.6,
                        borderRadius: "999px",
                        border: `1px solid ${mTokens.accent}55`,
                        backgroundColor: `${mTokens.accent}14`,
                        color: mTokens.accent,
                        maxWidth: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: "italic",
                          mr: 0.5,
                          color: mTokens.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedFileName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        sx={{
                          color: mTokens.error,
                        }}
                      >
                        <CancelOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
          </Box>
        </Box>
      </Box>
      <DocumentHistoryPopup
        onClose={handleHistoryPopup}
        documentData = {selectedHistoryData}
        open={showHistoryPopup}
        title="Archive Documents"
        downloadFile={true}
      />

      {cancelConfirm.show &&
        <OptionsPopup
          open={cancelConfirm.show}
          onClose={() => setCancelConfirm({ message: "", show: false, row: "" })}
          variant="confirm"
          message={<>{cancelConfirm.message}</>}
          buttons={[{ name: "Confirm", color: 'primary', onClick: confirmCancelIVR }, { name: "Cancel", color: 'secondary', onClick: cancelCancelIVR }]}
        />
      }
      {deleteConfirm.show &&
        <OptionsPopup
          open={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ message: "", show: false, row: "" })}
          variant="confirm"
          message={<>{deleteConfirm.message}</>}
          buttons={[{ name: "Confirm", color: 'primary', onClick: confirmDeleteIVR }, { name: "Cancel", color: 'secondary', onClick: cancelDeleteIVR }]}
        />
      }
    </Box>
  );
};

export default IVRDetails;